import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Archive, ArrowDown, ArrowUp, ArrowUpDown, CheckSquare, Edit2, Eye, Loader2, Plus, Search, Square, Star, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { bulkDeleteProducts, bulkUpdateProducts, deleteProduct, getAdminProducts, getImageUrl, setProductSaleStatus, setProductFeatured, updateProduct, CATEGORY_LABELS } from '../../config';
import { useAuth } from '../../context/AuthContext';

const STATUS_LABELS = {
  available: { label: 'Disponible', cls: 'bg-emerald-50 text-emerald-600' },
  reserved: { label: 'Reserve', cls: 'bg-amber-100 text-amber-600' },
  sold: { label: 'Vendu', cls: 'bg-gray-500/20 text-slate-600' },
};

const CONDITION_LABELS = {
  neuf: 'Neuf',
  reconditionne: 'Reconditionné',
};

const BULK_FIELDS = [
  { key: 'status', label: 'Statut', options: [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Reserve' },
    { value: 'sold', label: 'Vendu' },
  ]},
  { key: 'category', label: 'Categorie', options: Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label })) },
  { key: 'condition', label: 'Etat', options: [
    { value: 'reconditionne', label: 'Reconditionne' },
    { value: 'neuf', label: 'Neuf' },
  ]},
  { key: 'grade', label: 'Grade', options: [
    { value: 'A', label: 'Grade A' },
    { value: 'B', label: 'Grade B' },
    { value: 'C', label: 'Grade C' },
  ]},
  { key: 'archived', label: 'Archive', options: [
    { value: true, label: 'Oui' },
    { value: false, label: 'Non' },
  ]},
];

// Largeurs par défaut des colonnes (px). Le tech peut les redimensionner via
// le drag handle à droite de chaque header — sauvegardé en localStorage pour
// que la mise en page survive aux reload (feedback boss 2026-05-29).
// 3 presets de densité (boss feedback 2026-05-29). Remplace le drag handle
// qui n'a jamais fonctionné de manière fiable. Click sur un preset → la
// table se reformate instantanément, plus de tâtonnement.
const DENSITY_PRESETS = {
  compact: {
    select: 30,  name: 165,  category: 80,  condition: 80,  grade: 60,
    processor: 130, ram: 70, storage: 90,   screen: 80,    warranty: 80,
    repairability: 100, price: 85, status: 115, actions: 100,
  },
  standard: {
    select: 40,  name: 220,  category: 110, condition: 110, grade: 80,
    processor: 170, ram: 90, storage: 120,  screen: 110,   warranty: 110,
    repairability: 130, price: 110, status: 150, actions: 130,
  },
  comfortable: {
    select: 50,  name: 280,  category: 140, condition: 140, grade: 100,
    processor: 210, ram: 110, storage: 150, screen: 140,   warranty: 140,
    repairability: 160, price: 135, status: 185, actions: 165,
  },
};
const DEFAULT_COL_WIDTHS = DENSITY_PRESETS.standard;
const DENSITY_STORAGE_KEY = 'admin_products_density_v1';

function loadStoredDensity() {
  try {
    const raw = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (raw && DENSITY_PRESETS[raw]) return raw;
  } catch { /* ignore */ }
  return 'standard';
}

// Colonnes triables. Chaque entrée définit son extracteur de valeur pour
// le sort (string lowercase pour comparaison cohérente, number sinon).
const SORTABLE_COLUMNS = {
  name:      { label: 'Nom',       getter: p => (p.name || '').toLowerCase() },
  category:  { label: 'Catégorie', getter: p => (p.category || '').toLowerCase() },
  condition: { label: 'État',      getter: p => (p.condition || '').toLowerCase() },
  grade:     { label: 'Grade',     getter: p => (p.grade || 'Z').toLowerCase() },  // Z = sans grade en fin
  processor: { label: 'CPU',       getter: p => (p.processor || '').toLowerCase() },
  ram:       { label: 'RAM',       getter: p => (p.ram || '').toLowerCase() },
  storage:   { label: 'Stockage',  getter: p => (p.storage || '').toLowerCase() },
  screen:    { label: 'Écran',     getter: p => (p.screen_size || '').toLowerCase() },
  warranty:  { label: 'Garantie',  getter: p => (p.warranty || '').toLowerCase() },
  repairability: {
    label: 'Réparabilité',
    // Score moyen pour le tri. Non-scoré = -1 pour les pousser en fin de tri
    // asc (= mettre en évidence celles à remplir si on trie desc).
    getter: p => {
      if (p.repairability_status !== 'scored' || !p.repairability_scores) return -1;
      const vals = Object.values(p.repairability_scores).filter(v => typeof v === 'number');
      if (vals.length === 0) return -1;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    },
  },
  price:     { label: 'Prix TTC',  getter: p => Number(p.price) || 0 },
  status:    { label: 'Statut',    getter: p => (p.status || '').toLowerCase() },
};

export default function AdminProducts() {
  const { hasPermission } = useAuth();
  const canManageProducts = hasPermission('products:manage');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkField, setBulkField] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // ── Filtres + tri ─────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const [filterCondition, setFilterCondition] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState({ key: 'name', dir: 'asc' });

  // ── Densité d'affichage table (3 presets) ────────────────────────────────
  // Persistée en localStorage. Remplace l'ancien drag-handle qui ne
  // fonctionnait pas de manière fiable côté boss.
  const [density, setDensity] = useState(loadStoredDensity);
  useEffect(() => {
    try { localStorage.setItem(DENSITY_STORAGE_KEY, density); }
    catch { /* quota plein, tant pis */ }
  }, [density]);
  const colWidths = DENSITY_PRESETS[density] || DENSITY_PRESETS.standard;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminProducts(showArchived);
      setProducts(data.products || []);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [products]);

  // Liste effective après application des filtres + tri. Memoized pour ne pas
  // re-trier à chaque re-render (ex. toggle d'une checkbox sélection).
  const filteredSorted = useMemo(() => {
    const s = searchText.trim().toLowerCase();
    let list = products.filter(p => {
      if (s && !((p.name || '').toLowerCase().includes(s)
                  || (p.processor || '').toLowerCase().includes(s)
                  || (p.ram || '').toLowerCase().includes(s)
                  || (p.storage || '').toLowerCase().includes(s))) return false;
      if (filterCondition && p.condition !== filterCondition) return false;
      if (filterStatus && p.status !== filterStatus) return false;
      if (filterCategory && p.category !== filterCategory) return false;
      return true;
    });
    const col = SORTABLE_COLUMNS[sortBy.key];
    if (col) {
      const dir = sortBy.dir === 'desc' ? -1 : 1;
      list = [...list].sort((a, b) => {
        const va = col.getter(a);
        const vb = col.getter(b);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    }
    return list;
  }, [products, searchText, filterCondition, filterStatus, filterCategory, sortBy]);

  const handleSort = (key) => {
    setSortBy(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  const resetFilters = () => {
    setSearchText('');
    setFilterCondition('');
    setFilterStatus('');
    setFilterCategory('');
  };

  const hasActiveFilter = !!(searchText || filterCondition || filterStatus || filterCategory);

  // ── Sélection bulk ────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const visibleIds = filteredSorted.map(p => p.id);
    if (visibleIds.every(id => selected.has(id))) {
      // Tout visible est déjà sélectionné → tout désélectionner (parmi le visible)
      setSelected(prev => {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        visibleIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  // ── Édition inline du nom ─────────────────────────────────────────────────
  const handleNameChange = useCallback(async (id, newName) => {
    const trimmed = (newName || '').trim();
    if (!trimmed) return;
    setProducts(prev => prev.map(p => p.id === id ? { ...p, name: trimmed, _nameSaving: true } : p));
    try {
      await updateProduct(id, { name: trimmed });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, name: trimmed, _nameSaving: false } : p));
    } catch (err) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, _nameSaving: false, _nameError: err.message } : p));
      setTimeout(() => {
        setProducts(prev => prev.map(p => p.id === id ? { ...p, _nameError: null } : p));
      }, 3000);
    }
  }, []);

  // ── Édition inline du prix ────────────────────────────────────────────────
  const handlePriceChange = useCallback(async (id, newPrice) => {
    const value = parseFloat(newPrice);
    if (Number.isNaN(value) || value < 0) return;
    // Optimistic update : on update localement avant l'API
    setProducts(prev => prev.map(p => p.id === id ? { ...p, price: value, _priceSaving: true } : p));
    try {
      await updateProduct(id, { price: value });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, price: value, _priceSaving: false } : p));
    } catch (err) {
      // Rollback en cas d'échec
      setProducts(prev => prev.map(p => p.id === id ? { ...p, _priceSaving: false, _priceError: err.message } : p));
      setTimeout(() => {
        setProducts(prev => prev.map(p => p.id === id ? { ...p, _priceError: null } : p));
      }, 3000);
    }
  }, []);

  const handleDelete = async (id, name) => {
    if (!canManageProducts) return;
    if (!window.confirm(`Supprimer "${name}" ?`)) return;
    await deleteProduct(id);
    setProducts((current) => current.filter((product) => product.id !== id));
  };

  // ── Édition inline de l'état commercial (available/reserved/sold) ────────
  // Passe par /sale-status (pas /update générique) pour bénéficier des
  // horodatages sold_at/reserved_at et de l'annulation auto des réservations
  // client actives en cas de bascule manuelle.
  const handleStatusChange = useCallback(async (id, newStatus) => {
    if (!['available', 'reserved', 'sold'].includes(newStatus)) return;
    setProducts(prev => prev.map(p => p.id === id ? { ...p, status: newStatus, _statusSaving: true } : p));
    try {
      const updated = await setProductSaleStatus(id, newStatus);
      setProducts(prev => prev.map(p => p.id === id ? { ...p, ...updated, _statusSaving: false } : p));
    } catch (err) {
      setProducts(prev => prev.map(p => p.id === id ? { ...p, _statusSaving: false, _statusError: err.message } : p));
      setTimeout(() => {
        setProducts(prev => prev.map(p => p.id === id ? { ...p, _statusError: null } : p));
      }, 3000);
    }
  }, []);

  const handleArchive = async (id) => {
    if (!canManageProducts) return;
    await updateProduct(id, { archived: true });
    setProducts((current) => current.filter((product) => product.id !== id));
  };

  // Toggle « Sélection METERIS » (mise en avant). Optimiste + rollback si erreur.
  const handleToggleFeatured = async (product) => {
    if (!canManageProducts) return;
    const next = !product.featured;
    setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, featured: next } : p)));
    try {
      await setProductFeatured(product.id, next);
    } catch (err) {
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, featured: !next } : p)));
    }
  };

  const handleBulkDelete = async () => {
    if (!canManageProducts || selected.size === 0) return;
    if (!window.confirm(`Supprimer ${selected.size} produit(s) ?`)) return;
    setBulkLoading(true);
    try {
      await bulkDeleteProducts([...selected]);
      await load();
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (!canManageProducts || selected.size === 0 || !bulkField || bulkValue === '') return;
    const fieldDef = BULK_FIELDS.find((f) => f.key === bulkField);
    const displayValue = fieldDef?.options?.find((o) => String(o.value) === String(bulkValue))?.label || bulkValue;
    if (!window.confirm(`Modifier "${fieldDef?.label}" en "${displayValue}" pour ${selected.size} produit(s) ?`)) return;
    setBulkLoading(true);
    try {
      let parsedValue = bulkValue;
      if (bulkValue === 'true') parsedValue = true;
      else if (bulkValue === 'false') parsedValue = false;
      else if (!isNaN(Number(bulkValue)) && bulkField === 'price') parsedValue = Number(bulkValue);
      await bulkUpdateProducts([...selected], bulkField, parsedValue);
      await load();
    } finally {
      setBulkLoading(false);
      setBulkField('');
      setBulkValue('');
    }
  };

  const selectedFieldOptions = BULK_FIELDS.find((f) => f.key === bulkField)?.options || [];
  const allVisibleSelected = filteredSorted.length > 0 && filteredSorted.every(p => selected.has(p.id));

  return (
    <AdminLayout title="Produits">
      <div className="space-y-4">
        {/* Toolbar haut : filtres + actions */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="search"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Rechercher nom, CPU, RAM, stockage..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-sm focus:border-secondary outline-none"
                data-testid="admin-products-search"
              />
            </div>
            {/* Filtres select */}
            <select value={filterCondition} onChange={e => setFilterCondition(e.target.value)} className="bg-white border border-slate-200 rounded-sm px-3 py-2 text-sm text-primary focus:border-secondary outline-none">
              <option value="">Tous états</option>
              <option value="reconditionne">Reconditionné</option>
              <option value="neuf">Neuf</option>
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-white border border-slate-200 rounded-sm px-3 py-2 text-sm text-primary focus:border-secondary outline-none">
              <option value="">Tous statuts</option>
              <option value="available">Disponible</option>
              <option value="reserved">Réservé</option>
              <option value="sold">Vendu</option>
            </select>
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="bg-white border border-slate-200 rounded-sm px-3 py-2 text-sm text-primary focus:border-secondary outline-none">
              <option value="">Toutes catégories</option>
              {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                <option key={slug} value={slug}>{label}</option>
              ))}
            </select>
            {hasActiveFilter && (
              <button type="button" onClick={resetFilters} className="px-3 py-2 text-sm text-slate-600 hover:text-secondary border border-slate-200 rounded-sm" title="Reset filtres">
                <X className="w-4 h-4 inline" /> Filtres
              </button>
            )}
            {/* Densité — 3 presets qui reformat la table. Remplace l'ancien
                drag handle (jamais fonctionnel chez le boss). */}
            <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" role="radiogroup" aria-label="Densité d'affichage">
              <button
                type="button"
                role="radio"
                aria-checked={density === 'compact'}
                onClick={() => setDensity('compact')}
                className={`px-3 py-2 text-sm transition-colors ${density === 'compact' ? 'bg-secondary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                title="Densité compacte — tout voir d'un coup"
              >
                Compact
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={density === 'standard'}
                onClick={() => setDensity('standard')}
                className={`px-3 py-2 text-sm transition-colors border-l border-slate-200 ${density === 'standard' ? 'bg-secondary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                title="Densité standard"
              >
                Standard
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={density === 'comfortable'}
                onClick={() => setDensity('comfortable')}
                className={`px-3 py-2 text-sm transition-colors border-l border-slate-200 ${density === 'comfortable' ? 'bg-secondary text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                title="Densité aérée — plus lisible"
              >
                Aéré
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(event) => setShowArchived(event.target.checked)}
                  className="rounded bg-slate-50 border-slate-300 text-secondary focus:ring-secondary"
                />
                Inclure archives
              </label>
              <span className="text-xs text-slate-500">
                {filteredSorted.length} produit{filteredSorted.length > 1 ? 's' : ''}
                {filteredSorted.length !== products.length && ` (sur ${products.length})`}
              </span>
            </div>

            {canManageProducts && (
              <div className="flex items-center gap-2">
                <Link
                  to="/my/produits/importer"
                  className="inline-flex items-center gap-2 bg-secondary text-white font-bold uppercase tracking-wider rounded-sm px-5 py-2.5 text-sm hover:bg-secondary-hover transition-colors"
                >
                  <Search className="w-4 h-4" />
                  Importer (Icecat)
                </Link>
                <Link
                  to="/my/produits/nouveau"
                  className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-5 py-2.5 text-sm transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Nouveau produit
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Bulk actions bar */}
        {canManageProducts && selected.size > 0 && (
          <div className="bg-secondary/10 border border-secondary/40 rounded-sm p-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <span className="text-secondary text-sm font-medium shrink-0">
              {selected.size} selectionne(s)
            </span>

            <div className="flex flex-wrap items-center gap-2 flex-1">
              <select
                value={bulkField}
                onChange={(e) => { setBulkField(e.target.value); setBulkValue(''); }}
                className="bg-white border border-slate-200 rounded-sm px-3 py-1.5 text-sm text-primary focus:ring-secondary focus:border-secondary"
              >
                <option value="">Modifier un champ...</option>
                {BULK_FIELDS.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>

              {bulkField && selectedFieldOptions.length > 0 && (
                <select
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-1.5 text-sm text-primary focus:ring-secondary focus:border-secondary"
                >
                  <option value="">Valeur...</option>
                  {selectedFieldOptions.map((o) => (
                    <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
                  ))}
                </select>
              )}

              {bulkField && bulkValue !== '' && (
                <button
                  type="button"
                  onClick={handleBulkUpdate}
                  disabled={bulkLoading}
                  className="bg-secondary text-white font-bold uppercase tracking-wider rounded-sm px-4 py-1.5 text-sm hover:bg-secondary-hover transition-colors disabled:opacity-50"
                >
                  Appliquer
                </button>
              )}

              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={bulkLoading}
                className="bg-rose-100 text-rose-600 font-semibold rounded-sm px-4 py-1.5 text-sm hover:bg-red-500/30 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-3.5 h-3.5 inline mr-1" />
                Supprimer
              </button>

              <button
                type="button"
                onClick={() => { setSelected(new Set()); setBulkField(''); setBulkValue(''); }}
                className="text-slate-600 hover:text-secondary p-1.5 rounded-sm hover:bg-slate-100 transition-colors"
                title="Deselectionner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {bulkLoading && <Loader2 className="w-4 h-4 text-secondary animate-spin shrink-0" />}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-secondary animate-spin" />
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
            <p className="text-slate-500 mb-4">
              {hasActiveFilter ? 'Aucun produit ne correspond aux filtres' : 'Aucun produit'}
            </p>
            {hasActiveFilter ? (
              <button onClick={resetFilters} className="text-secondary hover:underline text-sm">
                Réinitialiser les filtres
              </button>
            ) : canManageProducts && (
              <Link to="/my/produits/nouveau" className="text-secondary hover:underline text-sm">
                Ajouter un produit
              </Link>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className={`text-sm products-density-${density}`} style={{ tableLayout: 'fixed', minWidth: '100%' }}>
                {/* <colgroup> + table-layout:fixed → les widths des colonnes sont
                    strictement respectées. Indispensable pour le resize manuel. */}
                <colgroup>
                  {canManageProducts && <col style={{ width: colWidths.select }} />}
                  <col style={{ width: colWidths.name }} />
                  <col style={{ width: colWidths.category }} className="hidden md:table-column" />
                  <col style={{ width: colWidths.condition }} className="hidden md:table-column" />
                  <col style={{ width: colWidths.grade }} className="hidden md:table-column" />
                  <col style={{ width: colWidths.processor }} className="hidden lg:table-column" />
                  <col style={{ width: colWidths.ram }} className="hidden lg:table-column" />
                  <col style={{ width: colWidths.storage }} className="hidden lg:table-column" />
                  <col style={{ width: colWidths.screen }} className="hidden xl:table-column" />
                  <col style={{ width: colWidths.warranty }} className="hidden xl:table-column" />
                  <col style={{ width: colWidths.repairability }} className="hidden md:table-column" />
                  <col style={{ width: colWidths.price }} />
                  <col style={{ width: colWidths.status }} />
                  <col style={{ width: colWidths.actions }} />
                </colgroup>
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    {canManageProducts && (
                      <th className="w-10 px-3 py-3 sticky left-0 bg-slate-50 z-10">
                        <button type="button" onClick={toggleAll} className="text-slate-600 hover:text-secondary transition-colors" title="Tout (dé)sélectionner sur le visible">
                          {allVisibleSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        </button>
                      </th>
                    )}
                    <SortableHeader columnKey="name" label="Produit" sortBy={sortBy} onSort={handleSort} sticky />
                    <SortableHeader columnKey="category" label="Catégorie" sortBy={sortBy} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader columnKey="condition" label="État" sortBy={sortBy} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader columnKey="grade" label="Grade" sortBy={sortBy} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader columnKey="processor" label="CPU" sortBy={sortBy} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortableHeader columnKey="ram" label="RAM" sortBy={sortBy} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortableHeader columnKey="storage" label="Stockage" sortBy={sortBy} onSort={handleSort} className="hidden lg:table-cell" />
                    <SortableHeader columnKey="screen" label="Écran" sortBy={sortBy} onSort={handleSort} className="hidden xl:table-cell" />
                    <SortableHeader columnKey="warranty" label="Garantie" sortBy={sortBy} onSort={handleSort} className="hidden xl:table-cell" />
                    <SortableHeader columnKey="repairability" label="♻️ Réparabilité" sortBy={sortBy} onSort={handleSort} className="hidden md:table-cell" />
                    <SortableHeader columnKey="price" label="Prix TTC" sortBy={sortBy} onSort={handleSort} />
                    <SortableHeader columnKey="status" label="Statut" sortBy={sortBy} onSort={handleSort} />
                    <th className="text-right px-4 py-3 text-xs text-slate-600 font-medium sticky right-0 bg-slate-50 z-10">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSorted.map((product) => {
                    const status = STATUS_LABELS[product.status] || STATUS_LABELS.available;
                    const isSelected = selected.has(product.id);
                    return (
                      <tr
                        key={product.id}
                        className={`border-b border-slate-200 last:border-0 transition-colors hover:bg-slate-50/60 ${product.archived ? 'opacity-50' : ''} ${isSelected ? 'bg-secondary/5' : ''}`}
                      >
                        {canManageProducts && (
                          <td className="w-10 px-3 py-3 sticky left-0 bg-white z-10">
                            <button type="button" onClick={() => toggleSelect(product.id)} className="text-slate-600 hover:text-secondary transition-colors">
                              {isSelected ? <CheckSquare className="w-4 h-4 text-secondary" /> : <Square className="w-4 h-4" />}
                            </button>
                          </td>
                        )}
                        <td className="px-4 py-3 sticky left-10 bg-white z-10">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-sm bg-slate-50 overflow-hidden shrink-0">
                              {product.images?.[0] ? (
                                <img src={getImageUrl(product.images[0])} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs">IMG</div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <EditableNameCell
                                product={product}
                                canEdit={canManageProducts}
                                onCommit={handleNameChange}
                              />
                              <p className="text-[10px] text-slate-500 mt-0.5">
                                {product.condition === 'neuf' ? 'Neuf' : 'Reconditionné'}
                                {product.grade ? ` · Grade ${product.grade}` : ''}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{CATEGORY_LABELS[product.category] || product.category}</td>
                        <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{CONDITION_LABELS[product.condition] || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{product.grade || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 hidden lg:table-cell truncate">{product.processor || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 hidden lg:table-cell truncate">{product.ram || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 hidden lg:table-cell truncate">{product.storage || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 hidden xl:table-cell truncate">{product.screen_size || '—'}</td>
                        <td className="px-4 py-3 text-slate-600 hidden xl:table-cell truncate">{product.warranty || '—'}</td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <RepairabilityBadgeAdmin product={product} />
                        </td>
                        <td className="px-4 py-3">
                          <EditablePriceCell
                            product={product}
                            canEdit={canManageProducts}
                            onCommit={handlePriceChange}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <EditableStatusCell
                            product={product}
                            canEdit={canManageProducts}
                            onCommit={handleStatusChange}
                          />
                        </td>
                        <td className="px-4 py-3 sticky right-0 bg-white z-10">
                          <div className="flex items-center justify-end gap-1">
                            <Link
                              to={`/produit/${product.id}`}
                              target="_blank"
                              className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-600 hover:text-secondary transition-colors"
                              title="Voir"
                            >
                              <Eye className="w-4 h-4" />
                            </Link>
                            {canManageProducts && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleToggleFeatured(product)}
                                  className={`p-1.5 rounded-sm hover:bg-slate-100 transition-colors ${product.featured ? 'text-secondary' : 'text-slate-400 hover:text-secondary'}`}
                                  title={product.featured ? 'Retirer de la sélection METERIS' : 'Mettre en avant (Sélection METERIS)'}
                                >
                                  <Star className={`w-4 h-4 ${product.featured ? 'fill-secondary' : ''}`} />
                                </button>
                                <Link
                                  to={`/my/produits/${product.id}/modifier`}
                                  className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-600 hover:text-secondary transition-colors"
                                  title="Modifier"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Link>
                                {product.status === 'sold' && (
                                  <button
                                    type="button"
                                    onClick={() => handleArchive(product.id)}
                                    className="p-1.5 rounded-sm hover:bg-slate-100 text-slate-600 hover:text-secondary transition-colors"
                                    title="Archiver"
                                  >
                                    <Archive className="w-4 h-4" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleDelete(product.id, product.name)}
                                  className="p-1.5 rounded-sm hover:bg-rose-50 text-slate-600 hover:text-rose-600 transition-colors"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

/**
 * Header de colonne triable. Click → bascule asc/desc/asc, et signale
 * visuellement la direction active via une icône arrow up/down.
 */
function SortableHeader({ columnKey, label, sortBy, onSort, className = '', sticky = false }) {
  const active = sortBy.key === columnKey;
  // Décision 2026-05-29 : suppression du drag handle. Il n'a jamais fonctionné
  // de manière fiable pour le boss (positionnement absolute dans <th> + sticky
  // + tableLayout fixed = combo capricieux), et provoquait des erreurs React
  // #418 via le role="separator" avec enfants. Remplacé par 3 presets de
  // densité (Compact / Standard / Confortable) qui couvrent le besoin réel
  // "je veux tout voir d'un coup".
  const stickyCls = sticky ? 'sticky left-10 bg-slate-50 z-10' : '';
  return (
    <th className={`text-left px-4 py-3 text-xs text-slate-600 font-medium ${stickyCls} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        className={`inline-flex items-center gap-1 hover:text-secondary transition-colors max-w-full overflow-hidden ${active ? 'text-secondary font-bold' : ''}`}
      >
        <span className="truncate">{label}</span>
        {active
          ? (sortBy.dir === 'asc' ? <ArrowUp className="w-3 h-3 shrink-0" /> : <ArrowDown className="w-3 h-3 shrink-0" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30 shrink-0" />}
      </button>
    </th>
  );
}

/**
 * Badge réparabilité 3-states pour l'admin liste (boss feedback 2026-05-29).
 * Distinct du badge public ProductCard.js qui est silencieux sous le seuil 7 :
 * ici on affiche aussi les scores < 7 (en orange) pour que le tech voie
 * d'un coup d'œil l'état réel — quitte à ce que le score ne sorte pas en
 * public. Vide (status≠scored ou pas de scores) = "—" gris pour signaler
 * une fiche à compléter.
 */
function RepairabilityBadgeAdmin({ product }) {
  if (product.repairability_status !== 'scored' || !product.repairability_scores) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-slate-400 italic" title="Non renseigné — à remplir dans la fiche">
        — Vide
      </span>
    );
  }
  const vals = Object.values(product.repairability_scores).filter(v => typeof v === 'number');
  if (vals.length === 0) {
    return <span className="inline-flex items-center gap-1 text-xs text-slate-400 italic">— Vide</span>;
  }
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  // Vert ≥ 7 (visible public) / Orange < 7 (rempli mais sous seuil — info tech only)
  const cls = avg >= 7
    ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
    : 'bg-amber-50 text-amber-700 border-amber-300';
  const tooltip = avg >= 7
    ? `Affiché côté client (≥ 7/10)`
    : `Renseigné mais sous le seuil 7/10 — masqué côté client`;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-sm border ${cls}`}
      title={tooltip}
    >
      ♻️ {avg.toFixed(1)}/10
    </span>
  );
}

/**
 * Cellule de nom éditable inline. Même pattern qu'EditablePriceCell :
 * click → input text, Enter/blur commit, Esc annule, optimistic update.
 * Permet au tech de corriger un nom moche directement depuis la liste
 * sans devoir ouvrir la fiche d'édition (boss feedback 2026-05-29).
 */
function EditableNameCell({ product, canEdit, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(product.name ?? '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  useEffect(() => { setValue(product.name ?? ''); }, [product.name]);

  if (!canEdit) {
    return (
      <p className="text-primary font-medium line-clamp-1" title={product.name}>{product.name}</p>
    );
  }

  const commit = () => {
    setEditing(false);
    const trimmed = value.trim();
    if (!trimmed || trimmed === product.name) {
      setValue(product.name ?? '');
      return;
    }
    onCommit(product.id, trimmed);
  };

  const cancel = () => {
    setValue(product.name ?? '');
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') cancel();
        }}
        className="w-full px-2 py-0.5 -mx-2 text-sm font-medium border border-secondary rounded-sm text-primary outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`group w-full text-left inline-flex items-center gap-1 px-2 py-0.5 -mx-2 rounded-sm hover:bg-secondary/10 transition-colors ${product._nameSaving ? 'opacity-60' : ''}`}
      title="Cliquer pour modifier le nom"
    >
      <span className="text-primary font-medium truncate flex-1">{product.name}</span>
      <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-60 text-slate-500 transition-opacity shrink-0" />
      {product._nameSaving && <Loader2 className="w-3 h-3 animate-spin text-secondary shrink-0" />}
      {product._nameError && <span className="ml-1 text-xs text-rose-600 shrink-0">❌</span>}
    </button>
  );
}

/**
 * Cellule de prix éditable inline. Click pour passer en édition, Enter ou
 * blur pour valider (commit API), Escape pour annuler. Optimistic update
 * avec rollback discret en cas d'erreur API (3 s).
 */
function EditablePriceCell({ product, canEdit, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(product.price ?? ''));
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  // Si le produit est rechargé (load API), on resync la valeur affichée.
  useEffect(() => { setValue(String(product.price ?? '')); }, [product.price]);

  if (!canEdit) {
    return <span className="font-medium text-primary">{product.price?.toFixed(2)} €</span>;
  }

  const commit = () => {
    setEditing(false);
    const newPrice = parseFloat(value);
    if (Number.isNaN(newPrice) || newPrice === product.price) {
      setValue(String(product.price ?? ''));
      return;
    }
    onCommit(product.id, newPrice);
  };

  const cancel = () => {
    setValue(String(product.price ?? ''));
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') cancel();
          }}
          className="w-24 px-2 py-1 text-sm border border-secondary rounded-sm text-primary outline-none"
        />
        <span className="text-xs text-slate-500">€</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`group inline-flex items-center gap-1 px-2 py-1 -mx-2 -my-1 rounded-sm font-medium text-primary hover:bg-secondary/10 transition-colors ${product._priceSaving ? 'opacity-60' : ''}`}
      title="Cliquer pour modifier"
    >
      {(product.price ?? 0).toFixed(2)} €
      <Edit2 className="w-3 h-3 opacity-0 group-hover:opacity-60 text-slate-500 transition-opacity" />
      {product._priceSaving && <Loader2 className="w-3 h-3 animate-spin text-secondary" />}
      {product._priceError && <span className="ml-1 text-xs text-rose-600">❌</span>}
    </button>
  );
}

/**
 * Cellule d'état commercial éditable inline.
 * <select> natif → un seul clic ouvre la liste navigateur, choix → commit
 * direct (pas de mode édition séparé). Pattern volontairement différent des
 * autres cellules éditables : avec 3 valeurs énumérées, le dropdown est plus
 * rapide et accessible (clavier/screen-reader gratuits) qu'un input texte.
 */
function EditableStatusCell({ product, canEdit, onCommit }) {
  const status = STATUS_LABELS[product.status] || STATUS_LABELS.available;

  if (!canEdit) {
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${status.cls}`}>
        {status.label}
      </span>
    );
  }

  const handleChange = (e) => {
    const next = e.target.value;
    if (next !== product.status) onCommit(product.id, next);
  };

  return (
    <div className="relative inline-flex items-center">
      <select
        value={product.status || 'available'}
        onChange={handleChange}
        disabled={product._statusSaving}
        className={`appearance-none text-xs font-medium pl-2 pr-6 py-0.5 rounded-full cursor-pointer border-0 outline-none focus:ring-2 focus:ring-secondary/40 ${status.cls} ${product._statusSaving ? 'opacity-60' : ''}`}
        title="Changer l'état commercial"
      >
        <option value="available">Disponible</option>
        <option value="reserved">Réservé</option>
        <option value="sold">Vendu</option>
      </select>
      {/* Chevron visuel pour signaler que c'est interactif */}
      <svg
        className="absolute right-1 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-60"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        aria-hidden
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
      {product._statusSaving && (
        <Loader2 className="w-3 h-3 animate-spin text-secondary ml-1.5" />
      )}
      {product._statusError && (
        <span className="ml-1.5 text-xs text-rose-600" title={product._statusError}>❌</span>
      )}
    </div>
  );
}
